import React, { useState, useRef } from 'react';
import { Scissors, Upload, Download, Play, Pause } from 'lucide-react';
import './ToolsCommon.css';

const VideoTrimmer = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isTrimming, setIsTrimming] = useState(false);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      setVideoUrl(url);
    }
  };

  const handleLoadedMetadata = () => {
    const videoDuration = videoRef.current.duration;
    setDuration(videoDuration);
    setEndTime(videoDuration);
  };

  const handleTimeUpdate = () => {
    const current = videoRef.current.currentTime;
    setCurrentTime(current);

    if (current >= endTime) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      if (videoRef.current.currentTime >= endTime) {
        videoRef.current.currentTime = startTime;
      }
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const trimVideo = async () => {
    setIsTrimming(true);

    // Note: Actual video trimming requires ffmpeg.wasm or server-side processing
    // This is a client-side limitation demo
    setTimeout(() => {
      alert(`Video would be trimmed from ${formatTime(startTime)} to ${formatTime(endTime)}.\n\nNote: Actual video trimming requires ffmpeg.wasm library for client-side processing or a backend service. This demo shows the UI and controls.`);
      setIsTrimming(false);
    }, 1500);
  };

  return (
    <div className="tool-container">
      <div className="tool-content-wrapper">
        <div className="tool-header">
          <Scissors size={48} className="tool-icon" />
          <h1 className="tool-title">Video Trimmer</h1>
          <p className="tool-subtitle">Trim and cut your videos</p>
        </div>

        <div className="tool-panel">
          <div className="tool-section">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="video/*"
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="tool-button primary"
              style={{ width: '100%' }}
            >
              <Upload size={20} />
              Select Video
            </button>
          </div>

          {videoUrl && (
            <>
              <div className="tool-section">
                <video
                  ref={videoRef}
                  src={videoUrl}
                  onLoadedMetadata={handleLoadedMetadata}
                  onTimeUpdate={handleTimeUpdate}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  style={{ width: '100%', borderRadius: '12px', marginBottom: '16px' }}
                  controls
                />

                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <button onClick={togglePlayPause} className="tool-button secondary">
                    {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                    {isPlaying ? 'Pause' : 'Play'} Preview
                  </button>
                </div>
              </div>

              <div className="tool-grid">
                <div className="tool-section">
                  <label className="tool-label">Start Time: {formatTime(startTime)}</label>
                  <input
                    type="range"
                    min="0"
                    max={duration}
                    step="0.1"
                    value={startTime}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setStartTime(value);
                      if (value >= endTime) {
                        setEndTime(Math.min(value + 1, duration));
                      }
                      videoRef.current.currentTime = value;
                    }}
                    className="tool-range"
                  />
                </div>

                <div className="tool-section">
                  <label className="tool-label">End Time: {formatTime(endTime)}</label>
                  <input
                    type="range"
                    min="0"
                    max={duration}
                    step="0.1"
                    value={endTime}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setEndTime(value);
                      if (value <= startTime) {
                        setStartTime(Math.max(value - 1, 0));
                      }
                    }}
                    className="tool-range"
                  />
                </div>
              </div>

              <div className="file-info">
                <div className="file-info-item">
                  <div className="file-info-label">Original Duration</div>
                  <div className="file-info-value">{formatTime(duration)}</div>
                </div>
                <div className="file-info-item">
                  <div className="file-info-label">Trimmed Duration</div>
                  <div className="file-info-value">{formatTime(endTime - startTime)}</div>
                </div>
                <div className="file-info-item">
                  <div className="file-info-label">Current Time</div>
                  <div className="file-info-value">{formatTime(currentTime)}</div>
                </div>
              </div>

              <button
                onClick={trimVideo}
                disabled={isTrimming}
                className="tool-button primary"
                style={{ width: '100%', marginTop: '24px' }}
              >
                {isTrimming ? 'Trimming...' : <><Download size={20} /> Trim & Download</>}
              </button>

              <div style={{ marginTop: '16px', padding: '16px', background: '#fef3c7', borderRadius: '12px', fontSize: '14px', color: '#92400e' }}>
                <strong>Note:</strong> Actual video trimming requires ffmpeg.wasm for client-side processing. This demo shows the UI and preview functionality.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoTrimmer;
